import React, { useState } from 'react';
import { merge } from 'lodash';
import { logger, stats } from '@fiverr-private/obs';
import { LazyComponent } from '@fiverr-private/orca';
import { getContext } from '@fiverr-private/fiverr_context';
import { BUSINESS_TRIGGER_CTA, useSuccessSignCtaEvent } from '@fiverr-private/business_success_sign_modal';
import { ModalType, openFipMigrationModal, shouldDisplayFullMigrationModal } from '@fiverr-private/pro_migration';
import { userIsPending } from '../utils/user/userIsPending';

const lazyImportActivationModal = () =>
    import(/* webpackChunkName: 'UserActivationModal' */ '@fiverr-private/user_activation');

/** @type {typeof import('./useBusinessSignInSteps').useMigrate} */
const useMigrate = ({ id, onSuccess, openFibMigrationPayloadWithoutOnSuccess, onMigrateModalUnavailable }) => {
    const checkShouldMigrateUser = () => shouldDisplayFullMigrationModal();
    const openUserMigration = () => {
        openFipMigrationModal({
            variant: ModalType.FullMigration,
            payload: {
                ...openFibMigrationPayloadWithoutOnSuccess,
                onSuccess
            },
            callbackWhenNotMounted: onMigrateModalUnavailable
        });
    };

    useSuccessSignCtaEvent({
        source: id,
        type: BUSINESS_TRIGGER_CTA.MIGRATION,
        callback: onSuccess
    });

    return { openUserMigration, checkShouldMigrateUser };
};

/** @type {typeof import('./useBusinessSignInSteps').useSignIn} */
const useSignIn = ({ id, onSuccess, signInModalParams }) => {
    const { businessTriggerCtaType, modalOption, biEnrichmentParams, source = 'business_sign_in_steps' } = signInModalParams;

    useSuccessSignCtaEvent({
        type: businessTriggerCtaType,
        source: id,
        callback: onSuccess
    });

    const checkShouldSignIn = () => !getContext().userId;

    const signIn = () => {
        const defaultOptions = {
            source,
            triggerCta: {
                type: businessTriggerCtaType,
                source: id
            }
        };
        const options = merge(defaultOptions, modalOption);
        window.openSignUpModal?.(options, biEnrichmentParams);
    };

    return { signIn, checkShouldSignIn };
};

/** @type {typeof import('./useBusinessSignInSteps').useActivation} */
const useActivation = ({ biPageName, description }) => {
    const [isActivationModalOpen, setIsActivationModalOpen] = useState(false);

    const checkShouldActivateUser = () => userIsPending();
    const activateUser = () => {
        setIsActivationModalOpen(true);
    };

    const ActivationModalComponent = (props) => {
        const modalProps = {
            isOpen: isActivationModalOpen,
            onClose: () => setIsActivationModalOpen(false),
            pageName: biPageName,
            description,
            ...props
        };

        return (
            <LazyComponent componentProps={modalProps}
                lazyImport={lazyImportActivationModal}
                onError={(error) => {
                    logger.warn('Failed to lazy load "UserActivationModal"', { error });
                    stats.count('user_session.user_activation_modal', 'lazy_import_failed');
                    if (modalProps.onError) {
                        modalProps.onError(error);
                    }
                }}/>
        );
    };

    return { activateUser, ActivationModalComponent, checkShouldActivateUser };
};

/** @type {typeof import('./useBusinessSignInSteps').useBusinessSignInSteps} */
export const useBusinessSignInSteps = ({
    id,
    onSuccess,
    signInModalParams,
    activationModalParams,
    migrateModalParams,
    shouldActivate = true,
    shouldMigrate = true
}) => {
    const { signIn, checkShouldSignIn } = useSignIn({ id, signInModalParams, onSuccess });
    const { openUserMigration, checkShouldMigrateUser } = useMigrate({
        id,
        openFibMigrationPayloadWithoutOnSuccess: migrateModalParams.openFibMigrationPayload,
        onSuccess,
        onMigrateModalUnavailable: migrateModalParams.onMigrateModalUnavailable
    });
    const { activateUser, ActivationModalComponent, checkShouldActivateUser } = useActivation(activationModalParams);

    /**
     * Check If user is signed in, activated or need to migrate
     */
    async function isActivatedBusinessUser() {
        return !checkShouldSignIn() && !checkShouldMigrateUser() && !await checkShouldActivateUser();
    }

    /**
     * Check If user need any step and do it
     */
    async function signInSteps() {
        if (checkShouldSignIn()) {
            signIn();
        } else if (shouldActivate && (await checkShouldActivateUser())) {
            activateUser();
        } else if (shouldMigrate && checkShouldMigrateUser()) {
            openUserMigration();
        } else {
            onSuccess();
        }
    }

    return { ActivationModalComponent, signInSteps, isActivatedBusinessUser };
};
